'use client'

import { useListItem } from '@floating-ui/react'
import React, { FC, PropsWithChildren } from 'react'

import { cn } from '../../utils/cn'

import { useSelectContext } from './select'
import { Check } from 'lucide-react'

interface SelectOptionProps {
  value: string
  children?: React.ReactNode
  className?: string
  hasIcon?: boolean
  hideCheck?: boolean
}

export const SelectOption: FC<PropsWithChildren<SelectOptionProps>> = ({
  value,
  children,
  className,
  hasIcon = false,
  hideCheck = false
}) => {
  const { activeIndex, selectedIndex, getItemProps, handleSelect } =
    useSelectContext()

  const { ref, index } = useListItem({ label: value })

  const isActive = activeIndex === index
  const isSelected = selectedIndex === index

  return (
    <button
      ref={ref}
      type="button"
      role="option"
      aria-selected={isSelected}
      tabIndex={isActive ? 0 : -1}
      {...getItemProps({
        onClick: () => handleSelect(index)
      })}
      className={cn(
        'relative flex cursor-pointer select-none items-center justify-between gap-x-2 rounded-lg p-2 text-sm outline-none transition-colors',
        'hover:bg-blue-100 focus:bg-blue-100 focus:outline-none',
        {
          'bg-slate-100': isSelected
        },
        className
      )}>
      <span className="flex items-center gap-x-2">
        {!hideCheck && isSelected && !hasIcon ? (
          <Check size={16} className="shrink-0" />
        ) : null}
        {children || value}
      </span>
      {!hideCheck && isSelected && hasIcon ? (
        <Check size={16} className="shrink-0" />
      ) : null}
    </button>
  )
}
